import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import { TrProvider } from './lib/tr';
import { SubdomainTenantProvider } from './lib/subdomain';

import { AuthCallback } from './pages/AuthPages';
import ChangePassword from './pages/ChangePassword';
import AdminSecurity from './pages/admin/AdminSecurity';
import OnboardBranding from './pages/OnboardBranding';
import AppLayout from './components/AppLayout';
import useNativeDeepLinks from './hooks/useNativeDeepLinks';

import { publicRoutes } from './routes/PublicRoutes';
import { platformRoutes } from './routes/PlatformRoutes';
import { staffRoutes } from './routes/StaffRoutes';
import { adminOnlyRoutes } from './routes/AdminOnlyRoutes';
import { patientRoutes } from './routes/PatientRoutes';
import { api } from './lib/api';

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-fg-muted">Loading…</div>;
  }
  if (!user) return <Navigate to="/sign-in" state={{ from: location }} replace />;
  const allowed = Array.isArray(role) ? role : (role ? [role] : null);
  // platform_admin is a superuser — always allowed (except when route is explicitly patient-only).
  const isPlatformAdmin = user.role === 'platform_admin';
  if (allowed && !allowed.includes(user.role) && !(isPlatformAdmin && !allowed.includes('patient'))) {
    const landing = user.role === 'patient' ? '/patient' : '/admin';
    return <Navigate to={landing} replace />;
  }
  // First-login password gate — newly-created staff must rotate their temp password.
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

// Iter 115 — Branding-required guard. New clinic admins must set up
// their logo + tagline + primary color before they can access the
// dashboard. Only applies to role=admin (clinicians/reception/patients
// bypass) and skips /onboard/branding + /change-password themselves.
function BrandingGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [state, setState] = useState({ checked: false, needs: false });

  useEffect(() => {
    let cancelled = false;
    if (!user || user.role !== 'admin') {
      setState({ checked: true, needs: false });
      return;
    }
    api.get('/clinic/settings')
      .then(r => {
        const s = r.data || {};
        // Iter 115 — backend exposes _has_custom_branding which reads the
        // per-tenant doc directly (bypasses the platform defaults so a
        // fresh clinic is correctly detected as un-branded).
        const hasBranding = !!s._has_custom_branding;
        if (!cancelled) setState({ checked: true, needs: !hasBranding });
      })
      .catch(() => { if (!cancelled) setState({ checked: true, needs: false }); });
    return () => { cancelled = true; };
  }, [user]);

  if (!state.checked) {
    return <div className="min-h-screen flex items-center justify-center text-fg-muted">Loading…</div>;
  }
  if (state.needs && location.pathname !== '/onboard/branding') {
    return <Navigate to="/onboard/branding" replace />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <TrProvider>
          <SubdomainTenantProvider>
            <BrowserRouter>
              <HashRouterWrapper />
              <Toaster position="top-right" richColors closeButton />
            </BrowserRouter>
          </SubdomainTenantProvider>
        </TrProvider>
      </AuthProvider>
    </div>
  );
}

function HashRouterWrapper() {
  // iter 80 — wire native deep links inside a router-aware component so
  // useNavigate() resolves. No-op on the web.
  useNativeDeepLinks();
  const hashCallback = typeof window !== 'undefined' && window.location.hash?.includes('session_id=');
  if (hashCallback) return <AuthCallback />;
  return (
    <Routes>
      {/* Public unauthenticated routes */}
      {publicRoutes}

      {/* Account-level routes any authenticated user can access. */}
      <Route path="/change-password" element={
        <Protected role={["admin","clinician","reception","patient"]}>
          <ChangePassword />
        </Protected>
      } />
      <Route element={<Protected role={["admin", "clinician", "reception", "patient"]}><AppLayout /></Protected>}>
        <Route path="/account/security" element={<AdminSecurity />} />
        {/* Iter 93 — patient-flavoured alias for the same Account & Privacy page. */}
        <Route path="/patient/account" element={<AdminSecurity />} />
      </Route>

      {/* Platform-admin — tenant management + revenue dashboard. */}
      <Route element={<Protected role={["platform_admin"]}><AppLayout /></Protected>}>
        {platformRoutes}
      </Route>

      {/* Iter 115 — Post-signup required branding wizard.
          Admins who don't yet have a logo + tagline are redirected here
          from any /admin/* or staff route by BrandingGuard. */}
      <Route path="/onboard/branding" element={
        <Protected role={["admin"]}>
          <OnboardBranding />
        </Protected>
      } />

      {/* Staff (admin / clinician / reception) — shared clinical screens. */}
      <Route element={<Protected role={["admin", "clinician", "reception"]}><BrandingGuard><AppLayout /></BrandingGuard></Protected>}>
        {staffRoutes}
      </Route>

      {/* Admin-only — finances, settings, marketing, etc. */}
      <Route element={<Protected role="admin"><BrandingGuard><AppLayout /></BrandingGuard></Protected>}>
        {adminOnlyRoutes}
      </Route>

      {/* Patient-only — self-service portal. */}
      <Route element={<Protected role="patient"><AppLayout /></Protected>}>
        {patientRoutes}
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
